import { useCallback, useMemo } from 'react';
import type { RefObject } from 'react';
import type { NextUp } from '../ListenContext.js';
import { bookKeypoints, preferredEdition } from '../bookLib.js';
import type { CatalogBook, CatalogKeypoint, Cefr, ChapterRef, Edition } from '../types.js';

export interface UseChapterNavReturn {
  hasNext: boolean;
  hasPrev: boolean;
  nextUp: NextUp | null;
  goNextChapter: () => void;
  /** Regla estándar: si `pos > 3s` reinicia el capítulo; si no, va al anterior. */
  goPrevChapter: () => void;
}

/**
 * Navegación de capítulos (cruza audiolibros, en orden de catálogo). Lista
 * plana ordenada de "paradas" (libro · edición · capítulo). Se usa la MISMA
 * edición que la abierta cuando el libro la tiene; si no, la preferida.
 */
export function useChapterNav(args: {
  catalog: CatalogBook[];
  ref: ChapterRef | null;
  audioRef: RefObject<HTMLAudioElement | null>;
  openChapter: (
    book: CatalogBook,
    chapter: CatalogKeypoint,
    edition: Edition,
    opts?: { play?: boolean; cefr?: Cefr },
  ) => Promise<void>;
  seek: (sec: number) => void;
}): UseChapterNavReturn {
  const { catalog, ref, audioRef, openChapter, seek } = args;

  const nav = useMemo(() => {
    const ed: Edition = ref?.edition ?? 'short';
    const stops: { book: CatalogBook; chapter: CatalogKeypoint; edition: Edition }[] = [];
    for (const b of catalog) {
      const useEd: Edition = b.editions[ed] ? ed : preferredEdition(b);
      for (const kp of bookKeypoints(b, useEd)) {
        stops.push({ book: b, chapter: kp, edition: useEd });
      }
    }
    const idx = ref
      ? stops.findIndex(
          (s) => s.book.slug === ref.book && s.edition === ref.edition && s.chapter.slug === ref.keypoint,
        )
      : -1;
    const next = idx >= 0 ? (stops[idx + 1] ?? null) : null;
    const prev = idx > 0 ? (stops[idx - 1] ?? null) : null;
    const nextUp: NextUp | null = next
      ? { book: next.book, chapter: next.chapter, edition: next.edition, crossBook: next.book.slug !== ref?.book }
      : null;
    return { next, prev, hasNext: next != null, hasPrev: prev != null, nextUp };
  }, [catalog, ref]);

  const goNextChapter = useCallback((): void => {
    if (nav.next) void openChapter(nav.next.book, nav.next.chapter, nav.next.edition, { play: true });
  }, [nav, openChapter]);

  // ⏮: si ya avanzaste >3s, reinicia el capítulo; si no, salta al anterior.
  const goPrevChapter = useCallback((): void => {
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      seek(0);
      return;
    }
    if (nav.prev) void openChapter(nav.prev.book, nav.prev.chapter, nav.prev.edition, { play: true });
    else seek(0);
  }, [nav, openChapter, seek, audioRef]);

  return {
    hasNext: nav.hasNext,
    hasPrev: nav.hasPrev,
    nextUp: nav.nextUp,
    goNextChapter,
    goPrevChapter,
  };
}
